import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
}


const SEOHead = () => {
  const location = useLocation();
  const { t, ready } = useTranslation();
  const currentPath = location.pathname;
  
  // Check if translations are actually loaded (not just keys)
  const isTranslationReady = () => {
    const testTranslation = t('seo.homepage.title', { returnObjects: false });
    return !testTranslation.includes('seo.') && testTranslation !== 'seo.homepage.title';
  };
  
  // Handle dynamic routes like /feedback/:id
  const basePath = currentPath.startsWith('/feedback') ? '/feedback' : currentPath;
  
  const getSeoData = (path: string): SEOData => {
    const baseUrl = 'https://newsforyou.ai';
    
    switch (path) {
      case '/':
        return {
          title: t('seo.homepage.title'),
          description: t('seo.homepage.description'),
          keywords: t('seo.homepage.keywords'),
          canonical: baseUrl
        };
      case '/subscribe':
        return {
          title: t('seo.subscribe.title'),
          description: t('seo.subscribe.description'),
          keywords: t('seo.subscribe.keywords'),
          canonical: `${baseUrl}/subscribe`
        };
      case '/preferences':
        return {
          title: t('seo.preferences.title'),
          description: t('seo.preferences.description'),
          keywords: t('seo.preferences.keywords'),
          canonical: `${baseUrl}/preferences`
        };
      case '/preferences/request':
        return {
          title: t('seo.preferencesRequest.title'),
          description: t('seo.preferencesRequest.description'),
          keywords: t('seo.preferencesRequest.keywords'),
          canonical: `${baseUrl}/preferences/request`
        };
      case '/feedback':
        return {
          title: t('seo.feedback.title'),
          description: t('seo.feedback.description'),
          keywords: t('seo.feedback.keywords'),
          canonical: `${baseUrl}/feedback`
        };
      case '/terms-of-use':
        return {
          title: t('termsOfUse.seo.title'),
          description: '', // No description for policy pages
          keywords: '', // No keywords for policy pages
        };
      default:
        return {
          title: t('seo.homepage.title'),
          description: t('seo.homepage.description'),
          keywords: t('seo.homepage.keywords'),
          canonical: baseUrl
        };
    }
  };
  
  const seoData = getSeoData(basePath);

  useEffect(() => {
    // Only update SEO when i18n is ready and initialized
    if (!ready || !i18n.isInitialized) {
      return;
    }

    // Check if translations are actually loaded (not just keys)
    if (!isTranslationReady()) {
      return;
    }

    // Update document title
    document.title = seoData.title;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoData.description);
    }

    // Add noindex for policy pages
    const isNoIndexPage = ['/terms-of-use', '/privacy-policy', '/cookie-policy'].includes(currentPath);
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', isNoIndexPage ? 'noindex, nofollow' : 'index, follow');

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords && seoData.keywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    if (metaKeywords && seoData.keywords) {
      metaKeywords.setAttribute('content', seoData.keywords);
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seoData.title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seoData.description);
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    if (seoData.canonical) {
      ogUrl.setAttribute('content', seoData.canonical);
    }

    let ogSiteName = document.querySelector('meta[property="og:site_name"]');
    if (!ogSiteName) {
      ogSiteName = document.createElement('meta');
      ogSiteName.setAttribute('property', 'og:site_name');
      ogSiteName.setAttribute('content', 'NewsForYou.ai');
      document.head.appendChild(ogSiteName);
    }

    let ogLocale = document.querySelector('meta[property="og:locale"]');
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      ogLocale.setAttribute('property', 'og:locale');
      ogLocale.setAttribute('content', 'pt_BR');
      document.head.appendChild(ogLocale);
    }

    // Update Open Graph image tags
    const updateOrCreateOGTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateOrCreateOGTag('og:image', 'https://newsforyou.ai/assets/news-for-you-smarter-news-banner.jpg');
    updateOrCreateOGTag('og:image:secure_url', 'https://newsforyou.ai/assets/news-for-you-smarter-news-banner.jpg');
    updateOrCreateOGTag('og:image:type', 'image/jpeg');
    updateOrCreateOGTag('og:image:width', '1200');
    updateOrCreateOGTag('og:image:height', '630');
    updateOrCreateOGTag('og:image:alt', 'NewsForYou.ai - Smarter news. Better you.');
    updateOrCreateOGTag('og:type', 'website');

    // Update Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', seoData.title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', seoData.description);
    }

    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', 'https://newsforyou.ai/assets/news-for-you-smarter-news-banner.jpg');
    }

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    if (seoData.canonical) {
      canonicalLink.setAttribute('href', seoData.canonical);
    }

  }, [currentPath, seoData, ready, i18n.isInitialized]);

  return null;
};

export default SEOHead;